class HimalayanHomeHero extends HTMLElement {
  #controller;
  #observer;
  #frame = 0;
  #reducedMotion;
  #planes = [];
  #copy;
  #light;
  #scroller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#planes = [...this.querySelectorAll('[data-depth]')];
    this.#copy = this.querySelector('.himalayan-home-hero__content');
    this.#light = this.querySelector('.himalayan-home-hero__light');
    this.#scroller = this.closest('.page-wrapper');
    document.addEventListener('scroll', this.#schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#measure, { passive: true, signal });
    document.addEventListener('visibilitychange', this.#schedule, { signal });
    this.#reducedMotion.addEventListener('change', this.#reset, { signal });
    this.addEventListener('pointermove', this.#point, { passive: true, signal });
    this.addEventListener('pointerleave', this.#leave, { signal });
    this.#observer = new ResizeObserver(this.#measure);
    const header = document.querySelector('#header-group');
    if (header) this.#observer.observe(header);
    this.#measure();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#clear();
  }

  #measure = () => {
    const scrollTop = this.#scroller ? this.#scroller.scrollTop - this.#scroller.getBoundingClientRect().top : window.scrollY;
    const top = Math.max(0, Math.round(this.getBoundingClientRect().top + scrollTop));
    this.style.setProperty('--himalayan-hero-top', `${top}px`);
    this.#schedule();
  };

  #schedule = () => {
    if (this.#frame || document.hidden) return;
    this.#frame = requestAnimationFrame(this.#paint);
  };

  #paint = () => {
    this.#frame = 0;
    if (!this.hasAttribute('data-motion') || this.#reducedMotion.matches) return;
    const box = this.getBoundingClientRect();
    if (!box.height || box.bottom < 0 || box.top > window.innerHeight) return;
    const progress = Math.max(0, Math.min(1, -box.top / box.height));
    for (const plane of this.#planes) {
      plane.style.transform = `translate3d(0, ${progress * Number(plane.dataset.depth)}%, 0)`;
    }
    if (this.#copy) {
      this.#copy.style.transform = `translate3d(0, ${progress * -52}px, 0)`;
      this.#copy.style.opacity = String(Math.max(0, 1 - progress * 1.7));
    }
  };

  #point = (event) => {
    if (!this.hasAttribute('data-motion') || this.#reducedMotion.matches || event.pointerType !== 'mouse' || !this.#light) return;
    const box = this.getBoundingClientRect();
    this.#light.style.transform = `translate3d(${event.clientX - box.left}px, ${event.clientY - box.top}px, 0)`;
    this.classList.add('is-touched');
  };

  #leave = () => this.classList.remove('is-touched');

  #clear = () => {
    for (const plane of this.#planes) plane.style.removeProperty('transform');
    this.#copy?.style.removeProperty('transform');
    this.#copy?.style.removeProperty('opacity');
    this.#light?.style.removeProperty('transform');
    this.#leave();
  };

  #reset = () => {
    this.#clear();
    this.#schedule();
  };
}

if (!customElements.get('himalayan-home-hero')) customElements.define('himalayan-home-hero', HimalayanHomeHero);
